import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { TpETLComponentTypeManifest } from '../interfaces';
import { TpETLObjectTypes } from '../../etl-data-types/interfaces';
import { color } from '../../../visual-analytics/helpers/colors';
import { TpETLPipelineComponent, TpETLDryRun, TpETLDryRunComponentResult } from '../../interfaces';
import styles from '../../edit-etl-component/editETLComponentForm.module.scss';
import ParamSet from '../../edit-etl-component/ParamSet';
import { CONFIGPARAMTYPES } from '../../../visual-analytics/helpers/configParams';
import { getETLComponentInputObjectTable } from '../../utils';
import { addETLDryRunError, getDryRunErrorsForComponent } from '../../etlDryRun';
import { FancyButton } from '../../../../components/primitives/FancyButton';
import { AutoComplete, TpAutoCompleteOption } from '../../../../components/auto-complete/AutoComplete';
import { get_guid } from '../../../visual-analytics/helpers/misc';

interface TpPropertyGroup {
  paramId: string;
  groupId: string;
  groupName: string;
  description: string;
  properties: string[];
}

function performDryRun(dryRun: TpETLDryRun, comp: TpETLPipelineComponent): TpETLDryRunComponentResult {
  let fail = false;
  const sourceTable = getETLComponentInputObjectTable(dryRun, comp, 'sourceTable');
  const sourceColumnIds = sourceTable.columns.map((col) => col.identifier);
  if (!comp.params['datatableId']) {
    addETLDryRunError(dryRun, `Missing DataTable ID`, comp.id, 'datatableId');
    fail = true;
  }

  // Check property groups: id must be unique and defined, name must be defined
  // Check that the property identifiers exist in the source table and there are no duplicate properties within and across groups
  const propertyGroups: TpPropertyGroup[] = comp.params['propertyGroups'] || [];
  const seenGroupIds: Set<string> = new Set();
  const seenProperties: Set<string> = new Set();
  for (const group of propertyGroups) {
    if (!group.groupId || group.groupId.trim() === '') {
      addETLDryRunError(dryRun, `Property group ID cannot be empty`, comp.id, group.paramId);
    } else if (seenGroupIds.has(group.groupId)) {
      addETLDryRunError(
        dryRun,
        `Property group ID must be unique: '${group.groupId}' is duplicated`,
        comp.id,
        group.paramId,
      );
    } else {
      seenGroupIds.add(group.groupId);
    }

    if (!group.groupName || group.groupName.trim() === '') {
      addETLDryRunError(dryRun, `Property group Name cannot be empty`, comp.id, group.paramId);
    }
    for (const propId of group.properties) {
      if (!sourceColumnIds.includes(propId)) {
        addETLDryRunError(
          dryRun,
          `Property identifier '${propId}' does not exist in source table`,
          comp.id,
          group.paramId,
        );
      } else if (seenProperties.has(propId)) {
        addETLDryRunError(
          dryRun,
          `Property identifier '${propId}' is already assigned (to another group)`,
          comp.id,
          group.paramId,
        );
      } else {
        seenProperties.add(propId);
      }
    }
  }

  return {
    abort: fail,
  };
}

function renderCustomConfigParams(
  dryRun: TpETLDryRun,
  comp: TpETLPipelineComponent,
  updateEditingComponent: (newComp: TpETLPipelineComponent) => void,
  ontology: any,
): any {
  const sourceTable = getETLComponentInputObjectTable(dryRun, comp, 'sourceTable');
  const propertyGroups: TpPropertyGroup[] = comp.params['propertyGroups'] || [];
  const propertyOptions: TpAutoCompleteOption[] = sourceTable.columns.map((col) => ({
    id: col.identifier,
    label: col.name || col.identifier,
  }));
  const unassignedOptions: TpAutoCompleteOption[] = propertyOptions.filter(
    (option) => !propertyGroups.some((group) => group.properties.includes(option.id)),
  );
  const ETLEntityTypes = Object.fromEntries(
    Object.entries(ontology.entityTypes)
      .filter(([key, value]) => value.isETLEntity === true)
      .filter(([key, value]) => sourceTable.columns.some((col) => col.identifier === value.primaryKey)),
  );

  function updateEntity(newValue: any) {
    comp.params['entityType'] = newValue;
    updateEditingComponent(comp);
  }

  function updateGroupParam(groupId: string, paramId: string, newValue: any) {
    const groupIdx = propertyGroups.map((group) => group.paramId).indexOf(groupId);
    if (groupIdx < 0) throw new Error(`Invalid property group id ${groupId}`);
    else if (paramId === 'groupId') propertyGroups[groupIdx].groupId = newValue;
    else if (paramId === 'groupName') propertyGroups[groupIdx].groupName = newValue;
    else if (paramId === 'description') propertyGroups[groupIdx].description = newValue;
    else if (paramId === 'properties') propertyGroups[groupIdx].properties = newValue;
    updateEditingComponent(comp);
  }

  function renderGroupErrors(group: TpPropertyGroup) {
    const errors = getDryRunErrorsForComponent(dryRun, comp.id).filter((error) => error.paramId === group.paramId);
    if (errors.length === 0) return null;
    return (
      <div className={styles.errors}>
        {errors.map((error: { message: string }, idx: number) => (
          <div key={`errors_${group.paramId}_${idx}`}>{error.message}</div>
        ))}
      </div>
    );
  }

  return (
    <>
      <div key={`filter_entity_type`} className={styles.section}>
        <h3>Bio|Verse Entity Type</h3>
        <div>
          Choose as which Entity Type this data should be imported into Bio|Verse <br />
          <b>
            <i>NOTE: Only ETL-compatibale Entities with their Primary Key in the Source Table are available</i>
          </b>
        </div>
        <ParamSet
          params={[
            {
              name: 'Entity type',
              component: (
                <select
                  value={comp.params['entityType']}
                  onChange={(ev) => {
                    updateEntity(ev.target.value);
                  }}
                >
                  <option key="_empty_" value={''}>
                    ---- Select Entity Type ----
                  </option>
                  {Object.entries(ETLEntityTypes).map(([entityType, entityTypeInfo]) => (
                    <option key={entityType} value={entityType}>
                      {entityTypeInfo.displayName}
                    </option>
                  ))}
                </select>
              ),
            },
          ]}
        />
      </div>

      <div key="property_groups" className={styles.section}>
        <h3>Property Groups</h3>
        <div className={styles.paramDescription}>
          Define property groups to organize imported properties in Bio|Verse. <br />
          The order of the property groups defines the order in which they will appear. Properties that are not assigned
          to any group are typically listed first. This component does not modify column order in the source table.
        </div>

        {propertyGroups.map((group) => (
          <div key={group.paramId} className={styles.section}>
            <h3>
              Property Group
              <button
                onClick={() => {
                  comp.params['propertyGroups'] = propertyGroups.filter(
                    (filterGroup) => group.paramId !== filterGroup.paramId,
                  );
                  updateEditingComponent(comp);
                }}
              >
                <FontAwesomeIcon icon="xmark" />
              </button>
            </h3>
            {renderGroupErrors(group)}
            <ParamSet
              params={[
                {
                  name: 'Group ID',
                  component: (
                    <input
                      value={group.groupId}
                      onChange={(ev) => {
                        updateGroupParam(group.paramId, 'groupId', ev.target.value);
                      }}
                    />
                  ),
                },
                {
                  name: 'Group Name',
                  component: (
                    <input
                      value={group.groupName}
                      onChange={(ev) => {
                        updateGroupParam(group.paramId, 'groupName', ev.target.value);
                      }}
                    />
                  ),
                },
                {
                  name: 'Group Description',
                  component: (
                    <input
                      value={group.description}
                      onChange={(ev) => {
                        updateGroupParam(group.paramId, 'description', ev.target.value);
                      }}
                    />
                  ),
                },
                {
                  name: 'Group Properties',
                  component: (() => {
                    const value: TpAutoCompleteOption[] = group.properties.map((propId) => {
                      const option = propertyOptions.find((opt) => opt.id === propId);
                      if (option) return option;
                      else
                        return {
                          id: propId,
                          label: propId,
                        };
                    });

                    return (
                      <AutoComplete
                        options={unassignedOptions}
                        value={value}
                        onChange={(next: TpAutoCompleteOption[]) => {
                          const ids = next.map((o: TpAutoCompleteOption) => o.id);
                          updateGroupParam(group.paramId, 'properties', ids);
                        }}
                        allowShowAll={true}
                        vertical={true}
                        placeholder="Select properties..."
                        classNames={{
                          container: styles.autocompleteContainer,
                          input: styles.autocompleteInput,
                          inputField: styles.autocompleteInputField,
                        }}
                      />
                    );
                  })(),
                },
              ]}
            />
          </div>
        ))}
        <FancyButton
          iconName="fas:plus"
          onClick={() => {
            const newPropertyGroup: TpPropertyGroup = {
              paramId: get_guid(),
              groupId: '',
              groupName: '',
              description: '',
              properties: [],
            };
            comp.params['propertyGroups'] = [...propertyGroups, newPropertyGroup];
            updateEditingComponent(comp);
          }}
        >
          Add property group
        </FancyButton>
      </div>
    </>
  );
}

export const manifestETLImportDataContext: TpETLComponentTypeManifest = {
  id: 'ImportDataContext',
  name: 'Import in Bio|Verse',
  description: 'Load the table into a BioVerse data context',
  categoryId: 'other',
  color: color(140, 0, 230),
  icon: <FontAwesomeIcon icon={['fas', 'chart-area']} />,
  useOntology: true,
  inputs: [
    {
      type: TpETLObjectTypes.TABLE,
      id: 'sourceTable',
      name: 'Source Table',
      columns: [],
    },
  ],
  outputs: [],
  params: [
    {
      id: 'createDashboard',
      name: 'Create Default dashboard',
      description: '',
      paramType: {
        configParamTypeId: CONFIGPARAMTYPES.Boolean,
        defaultValue: false,
        label: 'Flag that can be set to recreate the default templates',
      },
    },
    {
      id: 'datatableId',
      name: 'DataTable ID',
      description:
        'ID of the DataTable to import into. This ID is used in dashboard templates to identify the table and using an existing data object ID overwrites that table.',
      paramType: {
        configParamTypeId: CONFIGPARAMTYPES.String,
        defaultValue: '',
      },
    },
  ],
  performDryRun,
  renderCustomConfigParams,
};
